import axios, { AxiosError } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '@/utils/constants';

export type InvestigationType =
  | 'INSURANCE_CLAIMS'
  | 'CORPORATE_FRAUD'
  | 'BACKGROUND_CHECK'
  | 'OTHER';

export type Urgency = 'STANDARD' | 'PRIORITY' | 'URGENT';
export type SubjectType = 'PERSON' | 'ENTITY';
export type ReferralSource =
  | 'REFERRAL'
  | 'WEB_SEARCH'
  | 'EXISTING_CLIENT'
  | 'OTHER';

export interface PublicIntakeSubmission {
  investigationType: InvestigationType;
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterOrganization: string;
  requesterRole?: string;
  subjectType: SubjectType;
  subjectName: string;
  subjectAddress?: string;
  subjectDob?: string;
  subjectIdentifiers?: string;
  investigationDetails: string;
  desiredOutcome?: string;
  urgency: Urgency;
  howHeardAboutUs?: ReferralSource;
  // Honeypot — form renders this as a hidden field; real users leave it empty.
  website?: string;
}

export interface PublicIntakeResponse {
  referenceNumber: string;
  caseNumber: string;
  submittedAt: string;
}

/**
 * Dedicated axios instance for the public intake form. No auth interceptors,
 * no token refresh logic — the endpoint itself is unauthenticated.
 */
const publicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

interface PublicIntakeEnvelope {
  success: boolean;
  data?: PublicIntakeResponse;
  error?: { code: string; message: string; details?: unknown };
}

export async function submitPublicIntake(
  payload: PublicIntakeSubmission,
): Promise<PublicIntakeResponse> {
  try {
    const response = await publicClient.post<PublicIntakeEnvelope>(
      '/api/v1/public/investigations/intake',
      payload,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error?.message ?? 'Submission failed. Please try again.',
      );
    }

    return response.data.data;
  } catch (err) {
    // Surface server-provided messages for 4xx/5xx (rate limits, validation,
    // service errors) instead of axios' generic "Request failed with status code N".
    if (err instanceof AxiosError) {
      const data = err.response?.data as PublicIntakeEnvelope | undefined;
      const serverMessage = data?.error?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      if (err.response?.status === 429) {
        throw new Error(
          'Too many investigation requests from this IP. Please try again later.',
        );
      }
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        throw new Error(
          'We could not reach VisionFirm. Please check your connection and try again.',
        );
      }
    }
    throw err;
  }
}
